import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import Helmet from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const MythologicalNamesCalculator = () => {
  const [nameData, setNameData] = useState({
    name: '',
    mythology: '',
    deityType: '',
    symbolicMeaning: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNameData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const analyzeMythologicalName = () => {
    const { name, mythology, deityType, symbolicMeaning } = nameData;
    
    if (!name.trim()) {
      setError('Please enter a name');
      return;
    }

    // Function to generate mythological name analysis data
    const generateMythologicalData = (inputName, inputMythology, inputDeityType, inputSymbolicMeaning) => {
      const mythologies = ['Greek', 'Roman', 'Norse', 'Egyptian', 'Hindu'];
      const deityTypes = ['God', 'Goddess', 'Titan', 'Demi-god', 'Spirit'];
      const symbolicCategories = ['Nature', 'War', 'Love', 'Wisdom', 'Creation'];
      
      // Generate mythological distribution
      const mythologyDistribution = [];
      let totalPercentage = 0;

      while (totalPercentage < 100) {
        const origin = mythologies[Math.floor(Math.random() * mythologies.length)];
        const percentage = Math.floor(Math.random() * 30) + 1;
        mythologyDistribution.push({ origin, percentage });
        totalPercentage += percentage;
      }

      // Adjust last origin to make total 100%
      mythologyDistribution[mythologyDistribution.length - 1].percentage = 
        100 - (totalPercentage - mythologyDistribution[mythologyDistribution.length - 1].percentage);

      return {
        primaryMythology: inputMythology || mythologies[Math.floor(Math.random() * mythologies.length)],
        deityType: inputDeityType || deityTypes[Math.floor(Math.random() * deityTypes.length)],
        symbolicCategory: inputSymbolicMeaning || symbolicCategories[Math.floor(Math.random() * symbolicCategories.length)],
        mythologyDistribution,
        mythologicalSignificance: [
          { aspect: 'Symbolic', score: Math.floor(Math.random() * 10) + 1 },
          { aspect: 'Cultural', score: Math.floor(Math.random() * 10) + 1 },
          { aspect: 'Historical', score: Math.floor(Math.random() * 10) + 1 },
          { aspect: 'Literary', score: Math.floor(Math.random() * 10) + 1 },
          { aspect: 'Artistic', score: Math.floor(Math.random() * 10) + 1 }
        ],
        nameAnalysis: [
          { feature: 'Mythological Origin', description: 'The mythological tradition and cultural background' },
          { feature: 'Deity Classification', description: 'The type of divine being in mythology' },
          { feature: 'Symbolic Associations', description: 'The symbolic meanings and attributes' },
          { feature: 'Cultural Impact', description: 'The influence on art, literature, and culture' }
        ]
      };
    };

    const nameAnalysisData = generateMythologicalData(name, mythology, deityType, symbolicMeaning);

    // Prepare chart data
    const mythologyChartData = {
      labels: nameAnalysisData.mythologyDistribution.map(item => item.origin),
      datasets: [ {
        data: nameAnalysisData.mythologyDistribution.map(item => item.percentage),
        backgroundColor: [
          'rgba(153, 102, 255, 0.7)',
          'rgba(255, 159, 64, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(255, 99, 132, 0.7)',
          'rgba(54, 162, 235, 0.7)'
        ],
        borderColor: [
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)'
        ],
        borderWidth: 1
      } ]
    };

    const significanceChartData = {
      labels: nameAnalysisData.mythologicalSignificance.map(item => item.aspect),
      datasets: [ {
        label: 'Significance Score',
        data: nameAnalysisData.mythologicalSignificance.map(item => item.score),
        borderColor: 'rgb(153, 102, 255)',
        tension: 0.1,
        fill: false
      } ]
    };

    setResults(nameAnalysisData);
    setChartData({
      mythology: mythologyChartData,
      significance: significanceChartData
    });
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Mythological Names Calculator | Explore Divine Significance</title>
        <meta name="description" content="Analyze mythological origins, symbolic meanings, and cultural impact of names from various mythologies. Discover how names connect to ancient traditions and their enduring influence." />
        <meta name="keywords" content="mythological name calculator, deity classification, symbolic meaning, mythological tradition" />
        <meta property="og:title" content="Mythological Names Calculator | Explore Divine Significance" />
        <meta property="og:description" content="Analyze mythological origins, symbolic meanings, and cultural impact of names from various mythologies. Discover how names connect to ancient traditions and their enduring influence." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/mythological-names-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">⚡</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Mythological Names Calculator</h1>
            <p className="text-lg text-purple-600 font-semibold">Explore the Divine Significance of Mythological Names</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          Analyze the mythological origins, symbolic meanings, and cultural impact of names from various mythologies.
          Discover how names connect to ancient traditions and their enduring influence.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
            Name:
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={nameData.name}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter a mythological name"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="mythology">
            Mythological Tradition:
          </label>
          <input
            type="text"
            id="mythology"
            name="mythology"
            value={nameData.mythology}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="e.g., Greek, Roman, Norse"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="deityType">
            Deity Classification:
          </label>
          <input
            type="text"
            id="deityType"
            name="deityType"
            value={nameData.deityType}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="e.g., God, Goddess, Titan"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="symbolicMeaning">
            Symbolic Meaning:
          </label>
          <input
            type="text"
            id="symbolicMeaning"
            name="symbolicMeaning"
            value={nameData.symbolicMeaning}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="e.g., Nature, Love, Wisdom"
          />
        </div>

        <button
          onClick={analyzeMythologicalName}
          className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Analyze Mythological Significance
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <h4 className="text-xl font-semibold mb-4">Mythological Analysis</h4>
                <div className="space-y-4">
                  <div className="p-4 bg-white rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Primary Mythology:</span>
                      <span className="font-medium">{results.primaryMythology}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Deity Type:</span>
                      <span className="font-medium">{results.deityType}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Symbolic Category:</span>
                      <span className="font-medium">{results.symbolicCategory}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xl font-semibold mb-4">Mythology Distribution</h4>
                <div className="h-96">
                  <Pie
                    data={chartData.mythology}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Mythological Tradition Distribution'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-4">Mythological Significance</h4>
              <div className="space-y-4">
                <div className="h-96">
                  <Bar
                    data={chartData.significance}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        x: {
                          title: {
                            display: true,
                            text: 'Mythological Aspect'
                          }
                        },
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Significance Score'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Mythological Significance Scores'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-4">Name Analysis</h4>
              <div className="space-y-4">
                {results.nameAnalysis.map((analysis, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{analysis.feature}:</span>
                      <span className="font-medium">{analysis.description}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Example Analysis <span className="ml-2 text-2xl">📝</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Here's an example analysis of a mythological name:
          </p>
          
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="font-semibold mb-4">Sample Analysis:</h4>
            <ul className="list-disc pl-8 space-y-2">
              <li>Name: Athena</li>
              <li>Mythology: Greek</li>
              <li>Deity Type: Goddess</li>
              <li>Symbolic Meaning: Wisdom</li>
            </ul>
            
            <h4 className="font-semibold mt-6 mb-4">Analysis:</h4>
            <ol className="list-decimal pl-8 space-y-4">
              <li>
                <p>Mythological Origin:</p>
                <ul className="list-none pl-8 space-y-2">
                  <li>Primary Tradition: Greek</li>
                  <li>Historical Connections: Ancient Greece</li>
                  <li>Modern Significance: Symbol of wisdom</li>
                </ul>
              </li>
              <li>
                <p>Symbolic Significance:</p>
                <ul className="list-none pl-8 space-y-2">
                  <li>Attributes: Wisdom, strategy, warfare</li>
                  <li>Domains: Knowledge, crafts, defense</li>
                  <li>Modern Impact: Educational institutions</li>
                </ul>
              </li>
            </ol>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Mythological Names Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Mythological Names Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 1: Enter Name Information</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Input the mythological name you want to analyze</li>
                <li>Specify the mythological tradition if known</li>
                <li>Add the deity classification if applicable</li>
                <li>Include the symbolic meaning</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 2: Analyze Mythological Significance</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Click "Analyze Mythological Significance" button</li>
                <li>View the mythology distribution chart</li>
                <li>Explore mythological significance scores</li>
                <li>Discover name analysis</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 3: Interpret Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Review the primary mythological tradition</li>
                <li>Check the deity classification</li>
                <li>Understand symbolic categories</li>
                <li>Compare with other mythological names</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Important Considerations</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Mythological context varies by tradition</li>
                <li>Symbolic meanings can evolve over time</li>
                <li>Distribution reflects historical patterns</li>
                <li>Significance scores show mythological importance</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div className="faq-item bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">What is the Mythological Names Calculator?</h3>
              <p className="text-gray-700">
                The Mythological Names Calculator is a tool that analyzes names from various mythological traditions
                to identify their cultural origins, symbolic meanings, and historical significance. It helps users
                understand the mythological context and heritage associated with names from different traditions.
              </p>
            </div>

            <div className="faq-item bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">How accurate is the analysis?</h3>
              <p className="text-gray-700">
                The analysis provides general patterns and common associations based on mythological data.
                While it offers valuable insights, individual names may have unique histories or meanings
                not captured by the analysis.
              </p>
            </div>

            <div className="faq-item bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">What information do I need to provide?</h3>
              <p className="text-gray-700">
                You can provide:
                <ul className="list-disc pl-8 space-y-2 mt-2">
                  <li>The name you want to analyze</li>
                  <li>The mythological tradition (optional)</li>
                  <li>The deity classification (optional)</li>
                  <li>The symbolic meaning (optional)</li>
                </ul>
              </p>
            </div>

            <div className="faq-item bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">How should I interpret the results?</h3>
              <p className="text-gray-700">
                The results provide:
                <ul className="list-disc pl-8 space-y-2 mt-2">
                  <li>Primary mythological tradition</li>
                  <li>Mythology distribution across different traditions</li>
                  <li>Significance scores for various aspects</li>
                  <li>Detailed name analysis with mythological context</li>
                </ul>
              </p>
            </div>

            <div className="faq-item bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">What mythological traditions are supported?</h3>
              <p className="text-gray-700">
                The calculator supports names from various traditions:
                <ul className="list-disc pl-8 space-y-2 mt-2">
                  <li>Greek Mythology</li>
                  <li>Roman Mythology</li>
                  <li>Norse Mythology</li>
                  <li>Egyptian Mythology</li>
                  <li>Hindu Mythology</li>
                </ul>
              </p>
            </div>

            <div className="faq-item bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">What types of deities are included?</h3>
              <p className="text-gray-700">
                The calculator analyzes names based on different deity classifications:
                <ul className="list-disc pl-8 space-y-2 mt-2">
                  <li>Gods</li>
                  <li>Goddesses</li>
                  <li>Titans</li>
                  <li>Demi-gods</li>
                  <li>Spirits</li>
                </ul>
              </p>
            </div>

            <div className="faq-item bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">How are symbolic meanings determined?</h3>
              <p className="text-gray-700">
                Symbolic meanings are categorized based on common mythological attributes:
                <ul className="list-disc pl-8 space-y-2 mt-2">
                  <li>Nature</li>
                  <li>War</li>
                  <li>Love</li>
                  <li>Wisdom</li>
                  <li>Creation</li>
                </ul>
              </p>
            </div>

            <div className="faq-item bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">Can I analyze names from other mythologies?</h3>
              <p className="text-gray-700">
                While the calculator focuses on major mythological traditions, you can still analyze names from other
                traditions. The analysis will provide general patterns and common associations.
              </p>
            </div>

            <div className="faq-item bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">How can I use this information?</h3>
              <p className="text-gray-700">
                You can use this information to:
                <ul className="list-disc pl-8 space-y-2 mt-2">
                  <li>Learn about mythological traditions</li>
                  <li>Understand symbolic meanings</li>
                  <li>Explore cultural impact</li>
                  <li>Discover historical connections</li>
                  <li>Compare names across different mythologies</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Comprehensive Analysis</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Multi-dimensional mythological analysis</li>
                <li>Mythology distribution visualization</li>
                <li>Symbolic significance scoring</li>
                <li>Historical context exploration</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Interactive Features</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Dynamic charts and graphs</li>
                <li>Real-time updates</li>
                <li>Customizable input fields</li>
                <li>Responsive design</li>
              </ul>
            </div>

            <div className="col-span-2">
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Educational Value</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Learn about mythological traditions</li>
                <li>Understand symbolic meanings</li>
                <li>Explore cultural impact</li>
                <li>Discover historical connections</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Mythological Names <span className="ml-2 text-2xl">⚡</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Mythological names carry deep cultural and symbolic significance. Understanding them helps in:
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-purple-600 rounded-full mr-2"></span>
              <span className="font-medium">Cultural Heritage:</span> Tracing mythological traditions and influences
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Symbolic Meaning:</span> Understanding divine attributes and domains
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Historical Impact:</span> Exploring cultural evolution and influence
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Artistic Significance:</span> Analyzing representation in art and literature
            </li>
          </ul>
          <p className="italic text-gray-500 mt-6">
            Note: The analysis provides general mythological patterns and common associations. Individual names may have unique histories or meanings not captured by the analysis.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MythologicalNamesCalculator;